<script setup lang="ts">
    import BackIcon from "@/assets/back_blue.svg";
    import KButton from "@/components/Button.vue";
    import KSection from "@/components/generic/KSection.vue";
    import { CellContentWidth } from "@/components/KTable/helpers";
    import KTable from "@/components/KTable/KTable.vue";
    import { useStore } from "@/store";
    import CellLakemedel from "@/tabs/Receptforskrivning/cells/CellLakemedel.vue";
    import CellRekommenderat from "@/tabs/Receptforskrivning/cells/CellRekommenderat.vue";
    import ValtLakemedelDisplay from "@/tabs/Receptforskrivning/Forskrivning/Form/ValtLakemedel/ValtLakemedelDisplay.vue";
    import ValtLakemedelInfoDisplay from "@/tabs/Receptforskrivning/Forskrivning/Form/ValtLakemedel/ValtLakemedelInfoDisplay.vue";
    import { SilProductType } from "@/tabs/Receptforskrivning/helpers";
    import { LakemedelDisplay } from "@/tabs/Receptforskrivning/types";
    import { computed, inject, onMounted, ref } from "vue";

    const store = useStore();

    const props = defineProps<{
        mallId?: string;
    }>();

    const { Approved, NonAproved } = SilProductType;

    const columns: any = [
        {
            field: "lakemedel",
            header: "Läkemedel",
            minWidth: CellContentWidth.m,
            bodyTemplate: CellLakemedel,
        },
        {
            field: "rekommenderat",
            minWidth: CellContentWidth.icon,
            limitWidth: true,
            bodyTemplate: CellRekommenderat,
        },
        { field: "substans", header: "Substans", minWidth: CellContentWidth.xs },
        { field: "atc", header: "ATC", minWidth: CellContentWidth.xxs, limitWidth: true },
        { field: "styrka", header: "Styrka", minWidth: CellContentWidth.xs },
        { field: "form", header: "Form" },
        { field: "forpackning", header: "Förpackning", minWidth: CellContentWidth.m },
    ];

    const subsetName = ref<string | undefined>();
    const total = ref<number | null>(null);
    /**
     * Tidigare valda raden (läkemedlet) för att kunna jämföra mot nya valda och se om det förändrats
     */
    const previouslySelectedRow = ref<LakemedelDisplay | null>(null);
    /**
     * Valda raden (läkemedlet)
     */
    const selectedRow = ref<LakemedelDisplay | null>(
        store.getters["lakemedel/lakemedelById"]({
            displayMode: true,
            id: store.getters["receptforskrivningMall/mallById"]({
                id: props.mallId,
            })?.forskrivning?.items?.[0]?.lakemedelId,
        }) ?? null
    );
    const sokLakemedelRef = ref();
    const isLoading = ref(false);
    const hasSelectedLakemedel = ref(false);

    const lakemedel = computed(() =>
        store.getters["lakemedel/lakemedelDisplaySubset"]({
            subsetName: subsetName.value,
        })
    );

    const formRef: any = inject("formRef");

    const setSelectedLakemedel = async ({ reset }: { reset: boolean }) => {
        isLoading.value = true;

        const payload: {
            values: { [key: string]: unknown };
            reset: boolean;
        } = {
            reset,
            values: {
                lakemedelId: selectedRow.value?.id,
                nplId: selectedRow.value?.nplId,
            },
        };

        await store.dispatch("nll/fetchKodverkByLakemedel", {
            nplId: selectedRow.value?.nplId,
            lakemedelId: selectedRow?.value?.id,
        });

        previouslySelectedRow.value = selectedRow.value;
        formRef?.value?.setFormValues(payload);

        isLoading.value = false;
    };

    const onSearchLakemedel = async ({ query }: { query: string }) => {
        if (!query) {
            return;
        }

        try {
            const { subsetName: newSubsetName, total: newTotal } = await store.dispatch("lakemedel/fetchLakemedel", {
                filterParams: { query, product_type: formRef?.value?.getFieldValue({ name: "silProductType" }) },
            });

            subsetName.value = newSubsetName;
            total.value = newTotal;
        } catch (error) {
            return [];
        }
    };

    /**
     * Vid val av rad (läkemedel i tabellen)
     */

    const onSelectedRow = ({ value }: { value?: unknown }) => {
        selectedRow.value = (value as LakemedelDisplay) ?? null;
    };

    const onConfirm = async () => {
        if (!selectedRow.value) {
            return;
        }

        hasSelectedLakemedel.value = true;

        await setSelectedLakemedel({ reset: false });
    };

    const expand = () => {
        hasSelectedLakemedel.value = false;
    };

    defineExpose({
        // Plockas upp av KForm
        formListeners: [
            {
                fieldNames: ["silProductType"],
                action: () => {
                    // Nytt anrop om godkända/icke godkända har ändrats
                    onSearchLakemedel({ query: sokLakemedelRef?.value?.getSearchValue?.() });
                },
            },
        ] as Array<{
            fieldNames: string[];
            action: ({ values }: { values: { [key: string]: unknown } }) => void;
        }>,
    });

    onMounted(() => {
        if (props.mallId) {
            onConfirm();
        }
    });
</script>

<template>
    <KSection
        name="lakemedel"
        heading="Läkemedel"
        class="lakemedel-section"
        :number="2"
        :collapsible="false"
        :expanded="!hasSelectedLakemedel"
    >
        <template #collapsedContent v-if="selectedRow">
            <ValtLakemedelDisplay :lakemedel="selectedRow" />
            <KButton type="button" @click="expand" variant="contour" size="slim">
                <img :src="BackIcon" alt="Byt läkemedel" />
                <span>Byt läkemedel</span>
            </KButton>
        </template>
        <div class="form-content">
            <KRadio
                name="silProductType"
                :options="[
                    { label: 'Godkända läkemedel', value: Approved },
                    { label: 'Ej godkända läkemedel', value: NonAproved },
                ]"
                class="span-all"
            />
            <KAutocomplete
                ref="sokLakemedelRef"
                name="sokLakemedel"
                :options="[]"
                :onSearch="onSearchLakemedel"
                class="span-all"
                label="Sök läkemedel, styrka och ATC-kod"
                :keepSearchOnBlur="true"
                :hideDropdown="true"
            />
            <!-- lakemedelId är samma som nplPackId. Se typningen LakemedelDisplay -->
            <KText name="lakemedelId" validation="required" :hidden="true" />
            <KText name="nplId" validation="required" :hidden="true" />
            <div class="form-section span-all">
                <KTable
                    v-if="typeof total === 'number'"
                    name="lakemedel"
                    :value="lakemedel"
                    :columns="columns"
                    showSelectionToggle
                    class="span-all"
                    @selected="onSelectedRow"
                    :style="{ maxHeight: '30rem' }"
                    :expandableRows="true"
                    :multipleExpandableRows="false"
                    :expandOnRowClick="true"
                    :showExpandToggle="false"
                    :fullWidth="true"
                >
                    <template #expandedRow="{ data }">
                        <ValtLakemedelInfoDisplay :lakemedel="data" />
                    </template>
                </KTable>
            </div>
            <KButton
                v-if="typeof total === 'number'"
                type="button"
                @click="onConfirm"
                size="secondary"
                variant="standard"
                :isLoading="isLoading"
            >
                <span>Välj läkemedel</span>
            </KButton>
        </div>
    </KSection>
</template>

<style scoped lang="scss">
    @import "@/style/variables";

    .lakemedel-section {
    }
</style>
